import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faQuestion,
  faCheck,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import { CIRCUIT_STATES, ERROR_TYPES } from '../constants/circuit-breaker-constants.js';
import { useCircuitBreakerStatus } from '../hooks/useCircuitBreaker.js';
import { useTranslation } from '../context/TranslationContext.jsx';
import { getRouteConfigByRoute, getAllRoutes } from '../config/routes-config.js';
// Les styles CSS sont importés globalement via styles/index.js

/**
 * Composant d'affichage de l'état d'un circuit breaker
 * Affiche une icône et un indicateur d'état avec couleur de fond
 */
const CircuitBreakerStatus = ({ route, size, variant = 'standard', type = 'compact', className = '', style = {} }) => {
  const status = useCircuitBreakerStatus(route);
  const { t } = useTranslation();

  if (!status) {
    return null; // Circuit breaker non initialisé
  }

  /**
   * Obtenir l'icône pour le type de service
   */
  const getServiceIcon = (route) => {
    const routeConfig = getRouteConfigByRoute(route);
    const icon = routeConfig?.serviceIcon || faQuestion;
    return <FontAwesomeIcon icon={icon} />;
  };

  /**
   * Obtenir l'icône d'état avec Font Awesome
   */
  const getStatusIcon = (state, isHealthy, errorType, isStopped) => {
    if (isStopped) {
      return <FontAwesomeIcon icon={faClock} />; // En arrêt
    } else if (state === CIRCUIT_STATES.CLOSED && isHealthy) {
      return <FontAwesomeIcon icon={faCheck} />; // Checkmark
    } else if (state === CIRCUIT_STATES.HALF_OPEN) {
      return <FontAwesomeIcon icon={faClock} />; // En attente
    } else {
      // Utiliser la configuration centralisée des types d'erreurs
      const errorConfig = ERROR_TYPES[errorType] || ERROR_TYPES.unknown;
      return <FontAwesomeIcon icon={errorConfig.icon} />;
    }
  };

  /**
   * Obtenir les classes CSS pour le style
   */
  const getStatusClasses = (state, isHealthy, errorType, isStopped) => {
    if (isStopped) {
      return 'status-stopped';
    } else if (state === CIRCUIT_STATES.CLOSED && isHealthy) {
      return 'status-operational';
    } else if (state === CIRCUIT_STATES.HALF_OPEN) {
      return 'status-recovering';
    } else {
      // Utiliser la configuration centralisée des types d'erreurs
      const errorConfig = ERROR_TYPES[errorType] || ERROR_TYPES.unknown;
      return `status-error ${errorConfig.cssClass}`;
    }
  };

  /**
   * Obtenir le message de tooltip
   * @param {Object} status - L'objet status du circuit breaker
   * @param {string} separator - Le séparateur entre les éléments ('\n' par défaut, ' - ' en mode compact)
   */
  const getTooltipMessage = (status, separator = '\n') => {
    const { route, state, isHealthy, failures, lastHealthCheck, errorType, errorMessage, isStopped } = status;
    
    let stateMessage = '';
    if (isStopped) {
      stateMessage = t('CIRCUIT_BREAKER', 'SERVICE_STOPPED', 'Service arrêté');
    } else if (state === CIRCUIT_STATES.CLOSED && isHealthy) {
      stateMessage = t('CIRCUIT_BREAKER', 'SERVICE_OPERATIONAL', 'Service opérationnel');
    } else if (state === CIRCUIT_STATES.HALF_OPEN) {
      stateMessage = t('CIRCUIT_BREAKER', 'RECOVERY_TEST_IN_PROGRESS', 'Test de récupération en cours');
    } else {
      stateMessage = t('CIRCUIT_BREAKER', 'SERVICE_FAILURE', 'Service en défaillance');
      
      // Ajouter le type d'erreur au message de statut
      if (errorType) {
        const errorTypeMessage = getErrorTypeMessage(errorType);
        stateMessage += ` (${errorTypeMessage})`;
      }
    }

    const serviceName = getServiceName(route);
    let tooltip = `${serviceName}: ${stateMessage}`;
    
    if (failures > 0) {
      const failuresLabel = t('CIRCUIT_BREAKER', 'TOOLTIP_FAILURES', 'Échecs');
      tooltip += `${separator}${failuresLabel}: ${failures}`;
    }
    
    if (errorMessage) {
      const errorLabel = t('CIRCUIT_BREAKER', 'TOOLTIP_ERROR', 'Erreur');
      tooltip += `${separator}${errorLabel}: ${errorMessage}`;
    }
    
    if (lastHealthCheck) {
      const lastCheckLabel = t('CIRCUIT_BREAKER', 'TOOLTIP_LAST_CHECK', 'Dernière vérification');
      tooltip += `${separator}${lastCheckLabel}: ${new Date(lastHealthCheck).toLocaleTimeString()}`;
    }

    return tooltip;
  };

  /**
   * Obtenir le message descriptif du type d'erreur
   */
  const getErrorTypeMessage = (errorType) => {
    const errorConfig = ERROR_TYPES[errorType] || ERROR_TYPES.unknown;
    return t('CIRCUIT_BREAKER', errorConfig.translationKey, errorConfig.defaultText);
  };

  /**
   * Obtenir le nom du service
   */
  const getServiceName = (route) => {
    const routeConfig = getRouteConfigByRoute(route);
    if (!routeConfig) {
      return t('CIRCUIT_BREAKER', 'SERVICE_UNKNOWN', 'Service inconnu');
    }
    return t('CIRCUIT_BREAKER', routeConfig.translationKey, routeConfig.displayName);
  };

  const serviceIcon = getServiceIcon(route);
  const statusIcon = getStatusIcon(status.state, status.isHealthy, status.errorType, status.isStopped);
  const statusClasses = getStatusClasses(status.state, status.isHealthy, status.errorType, status.isStopped);
  const tooltipMessage = getTooltipMessage(status);
  const compactTooltipMessage = type === 'large' ? getTooltipMessage(status, ' - ') : null;

  // Construction des classes CSS avec la taille et la variante
  const getSizeClass = (size) => {
    switch (size) {
      case 'sm':
        return 'circuit-breaker--sm';
      case 'md':
        return 'circuit-breaker--md';
      case 'lg':
        return 'circuit-breaker--lg';
      default:
        return ''; // Utilise les styles par défaut
    }
  };

  const getVariantClass = (variant) => {
    switch (variant) {
      case 'ghost':
        return 'circuit-breaker--ghost';
      case 'standard':
      default:
        return ''; // Utilise les styles par défaut
    }
  };

  const sizeClass = getSizeClass(size);
  const variantClass = getVariantClass(variant);
  const combinedClasses = [
    'circuit-breaker-status',
    statusClasses,
    sizeClass,
    variantClass,
    className
  ].filter(Boolean).join(' ');

  if (type === 'large') {
    // Mode large : affichage en ligne avec le message de statut
    return (
      <span
        className={combinedClasses}
        style={style}
        role="status"
        aria-label={tooltipMessage}
      >
        <span className="service-icon" aria-hidden="true">
          {serviceIcon}
        </span>
        <span className="icon-spacer" aria-hidden="true"></span>
        <span className="status-icon">
          {statusIcon}
        </span>
        {compactTooltipMessage && (
          <span className="status-message" style={{ marginLeft: '8px', whiteSpace: 'normal', wordWrap: 'break-word' }}>
            {compactTooltipMessage}
          </span>
        )}
      </span>
    );
  }

  // Mode compact : affichage avec tooltip (comportement par défaut)
  return (
    <span
      className={combinedClasses}
      style={style}
      title={tooltipMessage}
      role="status"
      aria-label={tooltipMessage}
    >
      <span className="service-icon" aria-hidden="true">
        {serviceIcon}
      </span>
      <span className="icon-spacer" aria-hidden="true"></span>
      <span className="status-icon">
        {statusIcon}
      </span>
    </span>
  );
};

CircuitBreakerStatus.propTypes = {
  /** Route du service à surveiller */
  route: PropTypes.string.isRequired,
  /** Taille du circuit breaker */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  /** Variante de style du circuit breaker */
  variant: PropTypes.oneOf(['standard', 'ghost']),
  /** Type d'affichage du circuit breaker */
  type: PropTypes.oneOf(['compact', 'large']),
  /** Classes CSS supplémentaires */
  className: PropTypes.string,
  /** Styles CSS inline */
  style: PropTypes.object,
};

/**
 * Composant pour afficher tous les circuit breakers
 */
export const CircuitBreakerDashboard = ({ size, variant = 'standard', type = 'compact', container = 'none', className = '', style = {} }) => {
  const routes = getAllRoutes();
  const [, setUpdateTrigger] = React.useState({});

  // Forcer une mise à jour périodiquement pour que les changements d'état
  // du circuit breaker soient réfléchis dans le dashboard
  // même si les listeners ne se déclenchent pas immédiatement
  React.useEffect(() => {
    const interval = setInterval(() => {
      setUpdateTrigger({});
    }, 3000); // Mise à jour toutes les 3 secondes

    return () => clearInterval(interval);
  }, []);

  // Construction des classes CSS pour le conteneur
  const getContainerClass = (container) => {
    switch (container) {
      case 'box-grey':
        return 'circuit-breaker-dashboard--box-grey';
      case 'none':
      default:
        return ''; // Pas de classe supplémentaire
    }
  };

  const containerClass = getContainerClass(container);
  const dashboardClasses = [
    'circuit-breaker-dashboard',
    containerClass,
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={dashboardClasses} style={style}>
      <div className="status-container">
        {routes.map(route => (
          <CircuitBreakerStatus key={route} route={route} size={size} variant={variant} type={type} />
        ))}
      </div>
    </div>
  );
};

CircuitBreakerDashboard.propTypes = {
  /** Taille des circuit breakers dans le dashboard */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  /** Variante de style des circuit breakers dans le dashboard */
  variant: PropTypes.oneOf(['standard', 'ghost']),
  /** Type d'affichage des circuit breakers dans le dashboard */
  type: PropTypes.oneOf(['compact', 'large']),
  /** Style du conteneur du dashboard */
  container: PropTypes.oneOf(['none', 'box-grey']),
  /** Classes CSS supplémentaires */
  className: PropTypes.string,
  /** Styles CSS inline */
  style: PropTypes.object,
};

export default CircuitBreakerStatus;
